// ChannelModal.jsx

/* ChannelModal.jsx */

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Constants
import { CHANNEL_TYPES } from 'channels/constants/channelTypes';
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';

// CSS
import './ChannelModal.css';

// Components
import { createChannel } from 'channels/services/ChannelWriteService';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * ChannelModal
 *
 * Captures a channel title and creates a space channel.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Close callback (cancel / dismiss).
 * @param {function} [onCreated] - Called after a channel is created successfully.
 *
 * @returns {ReactNode} Rendered channel modal.
 */
const ChannelModal = React.memo(({ isOpen, onClose, onCreated }) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // User Context
    const { user } = useUser();

    // Base Context
    const { activeBase } = useBase();

    // ----- ROUTING ----- //

    // Routing

    const navigate = useNavigate();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    // Form State

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [channelType, setChannelType] = useState(CHANNEL_TYPES.TEXT);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ----- MEMOS ----- //

    // Determine if the form can be submitted based on required fields and submission state

    const canSubmit = useMemo(() => {
        return Boolean(title.trim() && activeBase?.key && user?.key && !isSubmitting);
    }, [title, activeBase?.key, user?.key, isSubmitting]);

    const channelTypeOptions = useMemo(() => {
        const options = [
            {
                value: CHANNEL_TYPES.TEXT,
                label: 'Text Channel',
                iconKey: 'tag',
                note: 'Asynchronous conversations with searchable message history.',
            },
        ];

        // Live rooms surface gated off — restore Voice / Video create option when re-enabled.
        if (LIVE_ROOMS_SURFACE_ENABLED) {
            options.push({
                value: CHANNEL_TYPES.VOICE_VIDEO,
                label: 'Voice / Video',
                iconKey: 'video_camera_front',
                note: 'Live room for calls and collaboration with channel updates.',
            });
        }

        return options;
    }, []);

    const showChannelTypeField = channelTypeOptions.length > 1;

    // ----- HANDLERS ----- //

    /**
     * handleClose
     * 
     * Handles closing the modal, preventing close if a submission is in progress.
     */

    const handleClose = () => {
        if (isSubmitting) return;
        onClose && onClose();
    };

    /**
     * handleCreateChannel
     * 
     * Handles creating a new channel using the createChannel service, then navigates to the new channel.
     */
    const handleCreateChannel = async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            const created = await createChannel({
                baseKey: activeBase.key,
                userKey: user.key,
                userName: user.name || user.title || user.displayName || user.email || 'Member',
                title,
                topic: description,
                channelType,
            });

            const channelKey = created?.key;
            if (channelKey) {
                handleClose();
                onCreated?.();
                navigate(`/channels/${channelKey}`);
                setTitle('');
                setDescription('');
                setChannelType(CHANNEL_TYPES.TEXT);
            }
        } catch (error) {
            console.error('[ChannelModal] create channel error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ----- RENDER ----- //

    return (

        <Modal
          title="Channel"
          isOpen={isOpen}
          width={isMobile ? '100%' : 460}
          height="auto"
          onClose={handleClose}
        >
            <ModalForm>

                <ModalField>

                    <ModalLabel
                        label="Name"
                    />

                    <TextInput
                      value={title}
                      placeholder="Channel name"
                      autoFocus
                      onChange={(event) => setTitle(event.target.value)}
                    />

                    <ModalHint
                        hint="The display name of the channel."
                    />

                </ModalField>

                {showChannelTypeField ? (
                    <ModalField>

                        <ModalLabel
                            label="Type"
                        />

                        <SelectInput
                          style={{
                                backgroundColor: theme.input,
                                borderColor: theme.backgroundMuted,
                                color: theme.foreground,
                                width: '100%',
                            }}
                          value={channelType}
                          options={channelTypeOptions}
                          placeholder="Select a channel type"
                          onValueChange={(value) => setChannelType(value)}
                        />

                        <ModalHint
                            hint="The type of channel to create: Text Channel or Voice / Video."
                        />

                    </ModalField>
                ) : null}

                <ModalField>

                    <ModalLabel
                        label="Description"
                    />

                    <TextInput
                      value={description}
                      placeholder="What is this channel for?"
                      multiline
                      expandable
                      showCount
                      maxLength={280}
                      onChange={(event) => setDescription(event.target.value)}
                    />

                    <ModalHint
                        hint="Optional description to provide more information and context about the purpose of the channel."
                    />

                </ModalField>

            </ModalForm>

            <ModalButtons>
                <Button
                  iconKey="check"
                  label={isSubmitting ? 'Creating...' : 'Create Channel'}
                  enabled={canSubmit}
                  height="44px"
                  onClick={handleCreateChannel}
                />
            </ModalButtons>

        </Modal>
    );
});

// Dev Tools
ChannelModal.displayName = 'ChannelModal';

export default ChannelModal;
